import React, { useEffect, useState } from "react";
import { Box, Button, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { addNewPaymentMethod, editPaymentMethod, getPaymentMethods } from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { format } from "date-fns";
import { toast } from "react-toastify";

const PaymentMethod = () => {
    const {state} = useAuth();
    const [paymentMethods, setPaymentMethods] = useState<any>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentPaymentMethod, setCurrentPaymentMethod] = useState<any>(null);
    const [formData, setFormData] = useState({
        paymentMethodName: '',
        displayName: ''
    });
    const [formErrors, setFormErrors] = useState({
        paymentMethodName: '',
        displayName: ''
    });

    const handleAddClick = () => {
        setModalMode('add');
        setFormData({
            paymentMethodName: '',
            displayName: ''
        });
        setCurrentPaymentMethod(null);
        setFormErrors({ paymentMethodName: '', displayName: '' });
        setIsDialogOpen(true);
    };

    const handleEditClick = (params: any) => {
        setModalMode('edit');
        setCurrentPaymentMethod(params.row);
        setFormData({
            paymentMethodName: params.row.paymentMethodName,
            displayName: params.row.displayName || ''
        });
        setFormErrors({ paymentMethodName: '', displayName: '' });
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setFormData({
            paymentMethodName: '',
            displayName: ''
        });
        setCurrentPaymentMethod(null);
        setFormErrors({ paymentMethodName: '', displayName: '' });
    };

    const validateForm = () => {
        const errors = {
            paymentMethodName: '',
            displayName: ''
        };
        let isValid = true;

        if (!formData.paymentMethodName.trim()) {
            errors.paymentMethodName = 'Payment method name cannot be empty';
            isValid = false;
        }

        if (!formData.displayName.trim()) {
            errors.displayName = 'Display name cannot be empty';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        if (modalMode === 'add') {
            const response = await addNewPaymentMethod(
                state.user?.token,
                formData.paymentMethodName,
                formData.displayName
            );
            if (response) {
                toast.success(response.data);
                getPaymentMethods(state.user?.token).then(setPaymentMethods);
                handleDialogClose();
            } else {
                toast.error('Something went wrong!');
            }
        } else {
            if (currentPaymentMethod) {
                const response = await editPaymentMethod(
                    state.user?.token,
                    currentPaymentMethod.id,
                    formData.paymentMethodName,
                    formData.displayName
                );
                if (response) {
                    toast.success(response.data);
                    getPaymentMethods(state.user?.token).then(setPaymentMethods);
                    handleDialogClose();
                } else if (response === 403) {
                    toast.error('You are not authorized!');
                }
            }
        }
    };

    useEffect(() => {
        getPaymentMethods(state.user?.token).then(setPaymentMethods);
    }, []);

    const columns: any = [
        {
            field: "paymentMethodName",
            headerName: "Payment Method Name",
            flex: 1,
            renderCell: (params: any) => (
                <div>{params.row.paymentMethodName}</div>
            ),
        },
        {
            field: "displayName",
            headerName: "Display Name",
            flex: 1,
            renderCell: (params: any) => (
                <div>{params.row.displayName}</div>
            ),
        },
        {
            field: "createdDate",
            headerName: "Created Date",
            flex: 0.4,
            renderCell: (params: any) => (
                <div>
                    {format(new Date(params.row.createdDate || Date.now()), 'dd/MM/yyyy')}
                </div>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.6,
            renderCell: (params: any) => (
                <div>
                    <Button 
                        variant="contained" 
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params)}
                        style={{ marginRight: 8, backgroundColor: "#6c757d", boxShadow: "none" }}
                    >
                        Edit
                    </Button>
                </div>
            )
        },
    ];

    return (
        <Box sx={{ 
            p: 1,
            height: '100%',
            minHeight: '100vh'
        }}>
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 3, 
                    borderRadius: '16px',
                    backgroundColor: '#ffffff',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    alignItems: 'center',
                    mb: 3 
                }}>
                    <Button
                        variant="contained"
                        onClick={handleAddClick}
                        style={{boxShadow: 'none', marginRight: '10px'}}
                    >
                        New Payment Method
                    </Button>
                </Box>

                <Box
                    sx={{ 
                        width: "100%",
                        height: 700,
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            margin: "0 auto",
                            "@media (max-width: 800px)": {
                                overflowX: "auto",
                                margin: 1,
                                minWidth: "95%",
                            },
                        }}
                    >
                        <DataGrid
                            rows={paymentMethods}
                            columns={columns}
                            disableRowSelectionOnClick
                            sx={{
                                "@media (max-width: 700px)": {
                                    minWidth: "100%",
                                },
                                "& .MuiDataGrid-columnHeader": {
                                    color: "#333",
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    backgroundColor: '#f0f0f0',
                                },
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: "bold",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "#f1f1f1",
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Paper>

            <Dialog 
                open={isDialogOpen} 
                onClose={handleDialogClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {modalMode === 'add' ? 'Add New Payment Method' : 'Edit Payment Method'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Payment Method Name"
                        type="text"
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={formData.paymentMethodName}
                        onChange={(e) => {
                            setFormData({...formData, paymentMethodName: e.target.value});
                            setFormErrors({...formErrors, paymentMethodName: ''});
                        }}
                        error={!!formErrors.paymentMethodName}
                        helperText={formErrors.paymentMethodName}
                        sx={{ mt: 1, mb: 1 }}
                    />

                    <TextField
                        margin="dense"
                        label="Display Name"
                        type="text"
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={formData.displayName}
                        onChange={(e) => {
                            setFormData({...formData, displayName: e.target.value});
                            setFormErrors({...formErrors, displayName: ''});
                        }}
                        error={!!formErrors.displayName}
                        helperText={formErrors.displayName}
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" style={{boxShadow: 'none'}}>
                        {modalMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaymentMethod;
